import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const GeographicTargetingSection = ({ targeting, onUpdate, isEditing, onToggleEdit }) => {
  const [formData, setFormData] = useState(targeting);
  const [zipCodeInput, setZipCodeInput] = useState('');

  const states = [
    { value: 'AL', label: 'Alabama' },
    { value: 'AK', label: 'Alaska' },
    { value: 'AZ', label: 'Arizona' },
    { value: 'AR', label: 'Arkansas' },
    { value: 'CA', label: 'California' },
    { value: 'CO', label: 'Colorado' },
    { value: 'CT', label: 'Connecticut' },
    { value: 'DE', label: 'Delaware' },
    { value: 'FL', label: 'Florida' },
    { value: 'GA', label: 'Georgia' },
    { value: 'HI', label: 'Hawaii' },
    { value: 'ID', label: 'Idaho' },
    { value: 'IL', label: 'Illinois' },
    { value: 'IN', label: 'Indiana' },
    { value: 'IA', label: 'Iowa' },
    { value: 'KS', label: 'Kansas' },
    { value: 'KY', label: 'Kentucky' },
    { value: 'LA', label: 'Louisiana' },
    { value: 'ME', label: 'Maine' },
    { value: 'MD', label: 'Maryland' },
    { value: 'MA', label: 'Massachusetts' },
    { value: 'MI', label: 'Michigan' },
    { value: 'MN', label: 'Minnesota' },
    { value: 'MS', label: 'Mississippi' },
    { value: 'MO', label: 'Missouri' },
    { value: 'MT', label: 'Montana' },
    { value: 'NE', label: 'Nebraska' },
    { value: 'NV', label: 'Nevada' },
    { value: 'NH', label: 'New Hampshire' },
    { value: 'NJ', label: 'New Jersey' },
    { value: 'NM', label: 'New Mexico' },
    { value: 'NY', label: 'New York' },
    { value: 'NC', label: 'North Carolina' },
    { value: 'ND', label: 'North Dakota' },
    { value: 'OH', label: 'Ohio' },
    { value: 'OK', label: 'Oklahoma' },
    { value: 'OR', label: 'Oregon' },
    { value: 'PA', label: 'Pennsylvania' },
    { value: 'RI', label: 'Rhode Island' },
    { value: 'SC', label: 'South Carolina' },
    { value: 'SD', label: 'South Dakota' },
    { value: 'TN', label: 'Tennessee' },
    { value: 'TX', label: 'Texas' },
    { value: 'UT', label: 'Utah' },
    { value: 'VT', label: 'Vermont' },
    { value: 'VA', label: 'Virginia' },
    { value: 'WA', label: 'Washington' },
    { value: 'WV', label: 'West Virginia' },
    { value: 'WI', label: 'Wisconsin' },
    { value: 'WY', label: 'Wyoming' }
  ];

  const radiusOptions = [
    { value: 5, label: '5 miles' },
    { value: 10, label: '10 miles' },
    { value: 25, label: '25 miles' },
    { value: 50, label: '50 miles' },
    { value: 100, label: '100 miles' },
    { value: 250, label: '250 miles' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addZipCode = () => {
    if (zipCodeInput?.trim() && zipCodeInput?.length === 5) {
      const newZipCodes = [...(formData?.zipCodes || []), zipCodeInput?.trim()];
      handleInputChange('zipCodes', newZipCodes);
      setZipCodeInput('');
    }
  };

  const removeZipCode = (zipCode) => {
    const updatedZipCodes = formData?.zipCodes?.filter(zip => zip !== zipCode);
    handleInputChange('zipCodes', updatedZipCodes);
  };

  const handleSave = () => {
    onUpdate(formData);
    onToggleEdit();
  };

  const handleCancel = () => {
    setFormData(targeting);
    setZipCodeInput('');
    onToggleEdit();
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
            <Icon name="MapPin" size={20} className="text-success" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Geographic Targeting</h3>
            <p className="text-sm text-muted-foreground">Define your service areas and geographic preferences</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {isEditing ? (
            <>
              <Button variant="outline" size="sm" onClick={handleCancel}>
                Cancel
              </Button>
              <Button variant="default" size="sm" onClick={handleSave}>
                Save Changes
              </Button>
            </>
          ) : (
            <Button variant="outline" size="sm" onClick={onToggleEdit} iconName="Edit2">
              Edit Targeting
            </Button>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Geographic Configuration */}
        <div className="space-y-4">
          <Select
            label="Target States"
            description="Select states where you want to receive leads"
            options={states}
            value={formData?.targetStates}
            onChange={(value) => handleInputChange('targetStates', value)}
            disabled={!isEditing}
            multiple
            searchable
            required
          />

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Target ZIP Codes
            </label>
            <div className="flex space-x-2 mb-2">
              <Input
                type="text"
                placeholder="Enter 5-digit ZIP code"
                value={zipCodeInput}
                onChange={(e) => setZipCodeInput(e?.target?.value)}
                disabled={!isEditing}
                maxLength="5"
                className="flex-1"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={addZipCode}
                disabled={!isEditing || zipCodeInput?.length !== 5}
                iconName="Plus"
              >
                Add
              </Button>
            </div>
            <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
              {formData?.zipCodes?.map((zipCode) => (
                <div
                  key={zipCode}
                  className="flex items-center space-x-1 px-2 py-1 bg-muted rounded text-sm"
                >
                  <span>{zipCode}</span>
                  {isEditing && (
                    <button
                      onClick={() => removeZipCode(zipCode)}
                      className="text-muted-foreground hover:text-error"
                    >
                      <Icon name="X" size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <Select
            label="Service Radius"
            description="Maximum distance from your location"
            options={radiusOptions}
            value={formData?.serviceRadius}
            onChange={(value) => handleInputChange('serviceRadius', value)}
            disabled={!isEditing}
          />

          <div className="space-y-3">
            <h4 className="text-sm font-medium text-foreground">Geographic Preferences</h4>
            <Checkbox
              label="Urban Areas Only"
              checked={formData?.urbanOnly || false}
              onChange={(e) => handleInputChange('urbanOnly', e?.target?.checked)}
              disabled={!isEditing}
            />
            <Checkbox
              label="Exclude Rural Areas"
              checked={formData?.excludeRural || false}
              onChange={(e) => handleInputChange('excludeRural', e?.target?.checked)}
              disabled={!isEditing}
            />
            <Checkbox
              label="Include Suburban Areas"
              checked={formData?.includeSuburban || false}
              onChange={(e) => handleInputChange('includeSuburban', e?.target?.checked)}
              disabled={!isEditing}
            />
          </div>
        </div>

        {/* Interactive Map */}
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-medium text-foreground mb-2">Coverage Map</h4>
            <div className="w-full h-64 bg-muted rounded-lg overflow-hidden border">
              <iframe
                width="100%"
                height="100%"
                loading="lazy"
                title="Geographic Coverage Map"
                referrerPolicy="no-referrer-when-downgrade"
                src="https://www.google.com/maps?q=39.8283,-98.5795&z=4&output=embed"
                className="border-0"
              />
            </div>
          </div>

          {/* Coverage Statistics */}
          <div className="bg-muted/30 rounded-lg p-4">
            <h4 className="text-sm font-medium text-foreground mb-3">Coverage Statistics</h4>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">States Covered:</span>
                <span className="font-medium text-foreground">
                  {formData?.targetStates?.length || 0}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">ZIP Codes:</span>
                <span className="font-medium text-foreground">
                  {formData?.zipCodes?.length || 0}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Service Radius:</span>
                <span className="font-medium text-foreground">
                  {formData?.serviceRadius || 0} miles
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Est. Population:</span>
                <span className="font-medium text-foreground">2.4M</span>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="space-y-2">
            <Button
              variant="outline"
              size="sm"
              iconName="Download"
              disabled={!isEditing}
              className="w-full"
            >
              Import ZIP Codes from File
            </Button>
            <Button
              variant="outline"
              size="sm"
              iconName="MapPin"
              disabled={!isEditing}
              className="w-full"
            >
              Add Radius Around City
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeographicTargetingSection;